<template>
  <div class="page-root">
    <!-- Page Header -->
    <header class="page-header">
      <button class="dashboard-back-btn" @click="router.push('/dashboard')">
        <i class="pi pi-arrow-left"></i>
        <span>Dashboard</span>
      </button>
      <div class="page-title-row">
        <i class="pi pi-book page-title-icon"></i>
        <h1 class="page-title">SOP Documents</h1>
        <span class="page-count-badge">{{ filteredSOPs.length }} documents</span>
        <button v-if="canCreate" class="create-btn" @click="router.push('/sop-documents/create')">
          <i class="pi pi-plus"></i> New SOP
        </button>
      </div>
    </header>

    <!-- Filters -->
    <div class="filters-bar glass-card">
      <div class="filter-group flex-1">
        <label class="filter-label">Search</label>
        <div class="search-wrap">
          <i class="pi pi-search search-icon"></i>
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Search procedures, protocols, policies…"
            class="filter-input search-input"
            @input="onSearch"
          />
          <button v-if="searchTerm" class="clear-btn" @click="clearSearch">
            <i class="pi pi-times"></i>
          </button>
        </div>
      </div>
      <button class="reset-btn" @click="resetFilters">
        <i class="pi pi-refresh"></i> Reset
      </button>
    </div>

    <!-- Category Pills -->
    <div class="cat-pills-row">
      <button
        v-for="cat in categories"
        :key="cat"
        class="cat-pill"
        :class="{ active: selectedCategory === cat }"
        @click="selectCategory(cat)"
      >{{ cat }}</button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <i class="pi pi-spin pi-spinner text-2xl text-teal-500"></i>
      <span>Loading SOP documents…</span>
    </div>

    <!-- Empty -->
    <div v-else-if="filteredSOPs.length === 0" class="empty-state glass-card">
      <i class="pi pi-book text-4xl text-slate-300"></i>
      <p class="text-slate-500 mt-2">No documents found</p>
      <p class="text-slate-400 text-sm">Try adjusting the search or category filter</p>
    </div>

    <!-- SOP Grid -->
    <div v-else class="sop-grid">
      <div
        v-for="sop in pagedSOPs"
        :key="sop.Id"
        class="sop-card glass-card"
        @click="router.push('/sop-documents/' + sop.Id)"
      >
        <div class="sop-card-icon-row">
          <div class="doc-type-icon" :class="getDocTypeClass(sop.FullFilePath)">
            <i :class="getDocTypeIconClass(sop.FullFilePath)"></i>
            <span>{{ getDocTypeLabel(sop.FullFilePath) }}</span>
          </div>
          <span class="sop-version" v-if="sop.Version">
            {{ sop.Version.startsWith('v') ? sop.Version : 'v' + sop.Version }}
          </span>
        </div>
        <h3 class="sop-title">{{ sop.Title }}</h3>
        <div class="sop-meta-row">
          <span class="cat-badge">{{ sop.Category }}</span>
          <span v-if="sop.DepartmentName" class="dept-badge">{{ sop.DepartmentName }}</span>
        </div>
        <p v-if="sop.Description" class="sop-desc">{{ sop.Description }}</p>
        <div class="sop-card-footer">
          <span class="view-hint">
            <i class="pi pi-eye text-xs"></i> Click to view
          </span>
          <div class="card-actions" @click.stop>
            <a
              v-if="sop.FullFilePath"
              :href="sop.FullFilePath"
              target="_blank"
              class="action-btn download-btn"
              title="Download"
            >
              <i class="pi pi-download"></i>
            </a>
            <template v-if="canManageSOP">
              <button
                class="action-btn edit-btn"
                title="Edit"
                @click="router.push('/sop-documents/' + sop.Id + '/edit')"
              >
                <i class="pi pi-pencil"></i>
              </button>
              <button
                class="action-btn toggle-btn"
                :title="sop.IsActive ? 'Deactivate' : 'Activate'"
                :class="sop.IsActive ? 'active-on' : 'active-off'"
                @click="toggleActive(sop)"
              >
                <i :class="sop.IsActive ? 'pi pi-eye' : 'pi pi-eye-slash'"></i>
              </button>
              <button
                class="action-btn delete-btn"
                title="Delete"
                @click="confirmDelete(sop)"
              >
                <i class="pi pi-trash"></i>
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination-row">
      <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">
        <i class="pi pi-chevron-left"></i>
      </button>
      <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
      <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>

    <!-- Delete Confirmation -->
    <div v-if="deleteTarget" class="confirm-overlay" @click.self="deleteTarget = null">
      <div class="confirm-dialog">
        <div class="confirm-icon"><i class="pi pi-exclamation-triangle"></i></div>
        <h3 class="confirm-title">Delete SOP Document?</h3>
        <p class="confirm-body">
          "<strong>{{ deleteTarget.Title }}</strong>" will be permanently removed. This cannot be undone.
        </p>
        <div class="confirm-actions">
          <button class="conf-cancel-btn" @click="deleteTarget = null">Cancel</button>
          <button class="conf-delete-btn" :disabled="deleteInProgress" @click="doDelete">
            <i v-if="deleteInProgress" class="pi pi-spin pi-spinner"></i>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Document Viewer Modal -->
    <div v-if="viewerOpen" class="viewer-overlay" @click.self="closeViewer">
      <div class="viewer-modal">
        <div class="viewer-header">
          <div class="viewer-title-row">
            <div class="doc-type-icon sm" :class="getDocTypeClass(selectedSOP?.FullFilePath)">
              <i :class="getDocTypeIconClass(selectedSOP?.FullFilePath)"></i>
            </div>
            <div>
              <h2 class="viewer-title">{{ selectedSOP?.Title }}</h2>
              <div class="viewer-meta">
                <span class="cat-badge">{{ selectedSOP?.Category }}</span>
                <span v-if="selectedSOP?.Version" class="version-tag">
                  {{ selectedSOP.Version.startsWith('v') ? selectedSOP.Version : 'v' + selectedSOP.Version }}
                </span>
                <span v-if="selectedSOP?.DepartmentName" class="dept-badge">{{ selectedSOP.DepartmentName }}</span>
              </div>
            </div>
          </div>
          <div class="viewer-actions">
            <a
              v-if="selectedSOP?.FullFilePath"
              :href="selectedSOP.FullFilePath"
              target="_blank"
              class="viewer-download-btn"
              title="Download"
            >
              <i class="pi pi-download"></i> Download
            </a>
            <button class="viewer-close-btn" @click="closeViewer">
              <i class="pi pi-times"></i>
            </button>
          </div>
        </div>

        <!-- Document content area -->
        <div class="viewer-body">
          <!-- PDF Viewer -->
          <iframe
            v-if="selectedSOP?.FullFilePath && isPDF(selectedSOP.FullFilePath)"
            :src="selectedSOP.FullFilePath + '#toolbar=1&navpanes=0'"
            class="doc-iframe"
            title="PDF Viewer"
          ></iframe>

          <!-- Office Viewer (PPTX, PPT, DOC, DOCX) -->
          <iframe
            v-else-if="selectedSOP?.FullFilePath && isOfficeDoc(selectedSOP.FullFilePath)"
            :src="officeViewerUrl(selectedSOP.FullFilePath)"
            class="doc-iframe"
            title="Document Viewer"
          ></iframe>

          <!-- Image Viewer -->
          <div
            v-else-if="selectedSOP?.FullFilePath && isImage(selectedSOP.FullFilePath)"
            class="image-viewer"
          >
            <img :src="selectedSOP.FullFilePath" :alt="selectedSOP.Title" class="doc-image" />
          </div>

          <!-- No file / Unknown type -->
          <div v-else class="no-file-state">
            <i class="pi pi-file text-5xl text-slate-300"></i>
            <p class="text-slate-500 mt-3">{{ selectedSOP?.FullFilePath ? 'Cannot preview this file type' : 'No document attached' }}</p>
            <p v-if="selectedSOP?.Description" class="text-slate-400 text-sm mt-2 max-w-md text-center">{{ selectedSOP.Description }}</p>
            <a
              v-if="selectedSOP?.FullFilePath"
              :href="selectedSOP.FullFilePath"
              target="_blank"
              class="attach-link mt-4"
            >
              <i class="pi pi-download"></i> Download File
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  getSOPDocuments,
  getSOPDocumentsByCategory,
  searchSOPDocuments,
  activateSOPDocument,
  deleteSOPDocument,
  getDashboardUserContext
} from '../../services/dashboardService'
import { useCreatePermission } from '@/composables/useCreatePermission'

const router = useRouter()
const ctx = getDashboardUserContext()
const canManageSOP = ctx.canManageSOP
// Create is gated by the role's "Create" operation on the SOP Documents entity.
const { canCreate, resolve: resolveCreatePermission } = useCreatePermission(
  (name) => name.includes('sop')
)

const loading = ref(true)
const allSOPs = ref<any[]>([])
const displaySOPs = ref<any[]>([])
const searchTerm = ref('')
const selectedCategory = ref('All')
const currentPage = ref(1)
const PAGE_SIZE = 12
let searchTimer: ReturnType<typeof setTimeout> | null = null

const categories = ['All', 'Clinical', 'HR', 'Pharmacy', 'Emergency', 'Admin', 'Compliance']

const filteredSOPs = computed(() => {
  if (searchTerm.value.trim()) return displaySOPs.value
  if (selectedCategory.value === 'All') return allSOPs.value
  return allSOPs.value.filter(s => s.Category === selectedCategory.value)
})

const totalPages = computed(() => Math.ceil(filteredSOPs.value.length / PAGE_SIZE) || 1)
const pagedSOPs = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE
  return filteredSOPs.value.slice(start, start + PAGE_SIZE)
})

watch(filteredSOPs, () => { currentPage.value = 1 })

// Document type helpers
function ext(path?: string) {
  if (!path) return ''
  return path.split('.').pop()?.toLowerCase() ?? ''
}
function isPDF(path?: string) { return ext(path) === 'pdf' }
function isOfficeDoc(path?: string) {
  return ['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx'].includes(ext(path))
}
function isImage(path?: string) {
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext(path))
}
function officeViewerUrl(path: string) {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(path)}`
}

function getDocTypeLabel(path?: string) {
  const e = ext(path)
  if (!e) return 'DOC'
  return e.toUpperCase()
}
function getDocTypeClass(path?: string) {
  const e = ext(path)
  if (e === 'pdf') return 'type-pdf'
  if (['ppt', 'pptx'].includes(e)) return 'type-ppt'
  if (['doc', 'docx'].includes(e)) return 'type-doc'
  if (['xls', 'xlsx'].includes(e)) return 'type-xls'
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(e)) return 'type-img'
  return 'type-generic'
}
function getDocTypeIconClass(path?: string) {
  const e = ext(path)
  if (e === 'pdf') return 'pi pi-file-pdf'
  if (['ppt', 'pptx'].includes(e)) return 'pi pi-desktop'
  if (['doc', 'docx'].includes(e)) return 'pi pi-file-word'
  if (['xls', 'xlsx'].includes(e)) return 'pi pi-table'
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(e)) return 'pi pi-image'
  return 'pi pi-file'
}

// Viewer state
const viewerOpen = ref(false)
const selectedSOP = ref<any>(null)

function openViewer(sop: any) {
  selectedSOP.value = sop
  viewerOpen.value = true
}
function closeViewer() {
  viewerOpen.value = false
  selectedSOP.value = null
}

// Search with debounce
function onSearch() {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(async () => {
    if (!searchTerm.value.trim()) {
      displaySOPs.value = []
      return
    }
    const ctx = getDashboardUserContext()
    displaySOPs.value = await searchSOPDocuments(ctx.orgId, ctx.branchId, searchTerm.value.trim())
  }, 350)
}
function clearSearch() {
  searchTerm.value = ''
  displaySOPs.value = []
  if (searchTimer) clearTimeout(searchTimer)
}

async function selectCategory(cat: string) {
  selectedCategory.value = cat
  clearSearch()
  if (cat !== 'All') {
    const ctx = getDashboardUserContext()
    const items = await getSOPDocumentsByCategory(ctx.orgId, ctx.branchId, cat)
    // merge into allSOPs filtered view
    // Actually we already have allSOPs — just filter client-side
    void items
  }
}

function resetFilters() {
  clearSearch()
  selectedCategory.value = 'All'
}

// Activate / Deactivate
async function toggleActive(sop: any) {
  const newStatus = !sop.IsActive
  const ok = await activateSOPDocument(sop.Id, newStatus, ctx.userId)
  if (ok) sop.IsActive = newStatus
}

// Delete
const deleteTarget = ref<any>(null)
const deleteInProgress = ref(false)

function confirmDelete(sop: any) {
  deleteTarget.value = sop
}

async function doDelete() {
  if (!deleteTarget.value) return
  deleteInProgress.value = true
  const ok = await deleteSOPDocument(deleteTarget.value.Id, ctx.userId)
  if (ok) {
    allSOPs.value = allSOPs.value.filter(s => s.Id !== deleteTarget.value.Id)
    displaySOPs.value = displaySOPs.value.filter(s => s.Id !== deleteTarget.value.Id)
  }
  deleteInProgress.value = false
  deleteTarget.value = null
}

// Keyboard close
function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') { closeViewer(); deleteTarget.value = null }
}

onMounted(async () => {
  resolveCreatePermission()
  const ctx = getDashboardUserContext()
  allSOPs.value = await getSOPDocuments(ctx.orgId, ctx.branchId)
  loading.value = false
  document.addEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.page-root {
  min-height: 100vh;
  background: #f1f5f9;
  padding: 1.5rem;
  font-family: 'Inter', sans-serif;
}

/* Header */
.page-header { margin-bottom: 1.25rem; }
.dashboard-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 14px;
  color: #64748b;
  font-size: 0.85rem;
  cursor: pointer;
  margin-bottom: 0.75rem;
  transition: all 0.15s;
}
.dashboard-back-btn:hover { background: #f8fafc; color: #0d9488; border-color: #5eead4; }
.page-title-row { display: flex; align-items: center; gap: 0.75rem; }
.page-title-icon { font-size: 1.4rem; color: #0d9488; }
.create-btn { display: inline-flex; align-items: center; gap: 6px; background: #0d9488; color: #fff; border: none; border-radius: 8px; padding: 6px 14px; font-size: 0.85rem; cursor: pointer; margin-left: auto; transition: background 0.15s; }
.create-btn:hover { background: #0f766e; }
.page-title { font-size: 1.5rem; font-weight: 700; color: #0f172a; margin: 0; }
.page-count-badge {
  background: #ccfbf1;
  color: #0d9488;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 0.78rem;
  font-weight: 600;
}

/* Glass card */
.glass-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}

/* Filters */
.filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
}
.filter-group { display: flex; flex-direction: column; gap: 4px; }
.filter-group.flex-1 { flex: 1; min-width: 200px; }
.filter-label { font-size: 0.75rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
.filter-input {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 0.875rem;
  color: #1e293b;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s;
}
.filter-input:focus { border-color: #5eead4; background: #fff; }
.search-wrap { position: relative; }
.search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 0.8rem; }
.search-input { padding-left: 30px; width: 100%; }
.clear-btn { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #94a3b8; cursor: pointer; padding: 2px; }
.reset-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 16px;
  font-size: 0.85rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  align-self: flex-end;
}
.reset-btn:hover { background: #f0fdfa; color: #0d9488; border-color: #5eead4; }

/* Category pills */
.cat-pills-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1.25rem;
}
.cat-pill {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 0.8rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}
.cat-pill:hover { background: #f0fdfa; color: #0d9488; border-color: #5eead4; }
.cat-pill.active { background: #ccfbf1; border-color: #5eead4; color: #0d9488; font-weight: 600; }

/* States */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem;
  color: #64748b;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

/* SOP Grid */
.sop-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.sop-card {
  padding: 1.25rem;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s, transform 0.1s;
}
.sop-card:hover {
  border-color: #5eead4;
  box-shadow: 0 4px 12px rgba(13,148,136,0.12);
  transform: translateY(-1px);
}
.sop-card-icon-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

/* Doc type icons */
.doc-type-icon {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 700;
}
.doc-type-icon.sm { padding: 3px 8px; font-size: 0.7rem; }
.type-pdf { background: #fee2e2; color: #dc2626; }
.type-ppt { background: #ffedd5; color: #ea580c; }
.type-doc { background: #dbeafe; color: #1d4ed8; }
.type-xls { background: #dcfce7; color: #15803d; }
.type-img { background: #f3e8ff; color: #7c3aed; }
.type-generic { background: #f1f5f9; color: #64748b; }

.sop-version {
  background: #f1f5f9;
  color: #64748b;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 600;
}
.sop-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
}
.sop-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 0.5rem;
}
.cat-badge {
  background: #ede9fe;
  color: #6d28d9;
  border-radius: 10px;
  padding: 2px 10px;
  font-size: 0.72rem;
  font-weight: 600;
}
.dept-badge {
  background: #f1f5f9;
  color: #475569;
  border-radius: 10px;
  padding: 2px 10px;
  font-size: 0.72rem;
}
.sop-desc {
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.5;
  margin: 0 0 0.75rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.sop-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f1f5f9;
  padding-top: 0.625rem;
}
.view-hint { font-size: 0.78rem; color: #94a3b8; display: flex; align-items: center; gap: 4px; }
.card-actions { display: flex; align-items: center; gap: 5px; }
.action-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
  font-size: 0.75rem;
  text-decoration: none;
  transition: all 0.15s;
}
.download-btn { border-color: #5eead4; background: #f0fdfa; color: #0d9488; }
.download-btn:hover { background: #ccfbf1; }
.edit-btn:hover { background: #eff6ff; color: #2563eb; border-color: #93c5fd; }
.toggle-btn.active-on { background: #f0fdf4; color: #16a34a; border-color: #86efac; }
.toggle-btn.active-on:hover { background: #dcfce7; }
.toggle-btn.active-off { background: #fff7ed; color: #d97706; border-color: #fcd34d; }
.toggle-btn.active-off:hover { background: #fef3c7; }
.delete-btn:hover { background: #fef2f2; color: #dc2626; border-color: #fca5a5; }

/* Confirm dialog */
.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15,23,42,0.55);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}
.confirm-dialog {
  background: #fff;
  border-radius: 14px;
  padding: 2rem;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 20px 50px rgba(0,0,0,0.2);
  text-align: center;
}
.confirm-icon { font-size: 2.5rem; color: #f59e0b; margin-bottom: 0.75rem; }
.confirm-title { font-size: 1.1rem; font-weight: 700; color: #0f172a; margin: 0 0 0.5rem 0; }
.confirm-body { font-size: 0.875rem; color: #64748b; line-height: 1.6; margin-bottom: 1.5rem; }
.confirm-actions { display: flex; gap: 0.75rem; justify-content: center; }
.conf-cancel-btn {
  padding: 8px 24px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s;
}
.conf-cancel-btn:hover { background: #f1f5f9; }
.conf-delete-btn {
  padding: 8px 24px;
  border: none;
  border-radius: 8px;
  background: #dc2626;
  color: #fff;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
}
.conf-delete-btn:hover:not(:disabled) { background: #b91c1c; }
.conf-delete-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Pagination */
.pagination-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 0;
}
.page-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 14px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}
.page-btn:hover:not(:disabled) { background: #f0fdfa; color: #0d9488; border-color: #5eead4; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 0.85rem; color: #64748b; }

/* ─── Viewer Modal ─── */
.viewer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
}
.viewer-modal {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  overflow: hidden;
}
.viewer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  gap: 1rem;
  flex-shrink: 0;
}
.viewer-title-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.viewer-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.4rem 0;
}
.viewer-meta { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.version-tag {
  background: #f1f5f9;
  color: #64748b;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 600;
}
.viewer-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.viewer-download-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0fdfa;
  border: 1px solid #5eead4;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.85rem;
  color: #0d9488;
  text-decoration: none;
  transition: all 0.15s;
  cursor: pointer;
}
.viewer-download-btn:hover { background: #ccfbf1; }
.viewer-close-btn {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.15s;
}
.viewer-close-btn:hover { background: #fee2e2; color: #dc2626; border-color: #fecaca; }

.viewer-body {
  flex: 1;
  overflow: auto;
  background: #f8fafc;
  display: flex;
  align-items: stretch;
}
.doc-iframe {
  width: 100%;
  min-height: 600px;
  border: none;
  flex: 1;
}
.image-viewer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  width: 100%;
}
.doc-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
}
.no-file-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  width: 100%;
  text-align: center;
}
.attach-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  color: #0d9488;
  text-decoration: none;
  padding: 8px 18px;
  border: 1px solid #5eead4;
  border-radius: 8px;
  background: #f0fdfa;
  transition: all 0.15s;
}
.attach-link:hover { background: #ccfbf1; }
</style>
